import { parseSpriteState } from '../../../core/utils/hangoutUtils.js';
import { accumulateStateFromMessages, getLastRoundSpriteTuple } from './state-utils.js';
import { resolveCharacterRank } from './rank-utils.js';
import { normalizeGender } from '../../../js/utils/genderUtils.js';
import { getDefaultStandingActionId } from '../../../js/utils/spriteFallbacks.js';
import { isPlaceholderCharacterName } from './character-name-utils.js';
import { sortIdsByNumericAsc } from '../../../js/utils/characterIdSort.js';

const toCarouselCharacter = ({ id, name, rank, modalImage, outfitId, ...rest }) => ({
  id,
  name,
  rank,
  modalImage,
  outfitId,
  ...rest
});

export const getOrderedScenarioChars = (scenarioDetails) => {
  const scenarioChars = Array.isArray(scenarioDetails?.chars) ? scenarioDetails.chars : [];
  return scenarioChars
    .map((char, index) => ({ char, index }))
    .sort((a, b) => {
      const aPos = Number(a.char?.pos);
      const bPos = Number(b.char?.pos);
      const aHasPos = Number.isFinite(aPos);
      const bHasPos = Number.isFinite(bPos);

      if (aHasPos && bHasPos && aPos !== bPos) return aPos - bPos;
      if (aHasPos !== bHasPos) return aHasPos ? -1 : 1;
      return a.index - b.index;
    })
    .map(entry => entry.char);
};

export const buildCarouselCharacters = async ({
  primaryCharacter,
  scenarioDetails,
  service,
  rankingsState,
  selectedOutfitId,
  scenarioId,
  partyCharacterIds = null
}) => {
  const idsEqual = (a, b) => String(a) === String(b);
  const orderedScenarioChars = getOrderedScenarioChars(scenarioDetails);

  const hasPartyOverride = Array.isArray(partyCharacterIds) && partyCharacterIds.length > 0;
  let allChars;
  if (hasPartyOverride) {
    allChars = partyCharacterIds.slice(0, 3).map((id) => {
      const scenarioChar = orderedScenarioChars.find(c => idsEqual(c?.id, id));
      return scenarioChar ? { ...scenarioChar, id } : { id };
    });
  } else {
    const hasPrimaryInScenario = orderedScenarioChars.some(char => idsEqual(char?.id, primaryCharacter.id));
    allChars = hasPrimaryInScenario
      ? orderedScenarioChars.slice(0, 3)
      : [
        { id: primaryCharacter.id },
        ...orderedScenarioChars
          .filter(char => !idsEqual(char?.id, primaryCharacter.id))
          .slice(0, 2)
      ];
  }

  const scenarioOutfits = (scenarioDetails.outfits || [])
    .map(id => parseInt(String(id), 10))
    .filter(Number.isFinite);
  const defaultOutfitId = scenarioOutfits[0]?.toString() || '0';
  const rankBatch = {};

  const characterPromises = allChars.map(async (char) => {
    try {
      const charDetails = await service.getCharacterDetails(char.id);

      let characterOutfitId;
      if (idsEqual(char?.id, primaryCharacter.id)) {
        characterOutfitId = selectedOutfitId;
        if (!characterOutfitId || (scenarioOutfits.length > 0 && !scenarioOutfits.includes(parseInt(characterOutfitId)))) {
          characterOutfitId = defaultOutfitId;
        }
      } else {
        // Use the outfit stored on this char entry, falling back to default
        const scenarioChar = orderedScenarioChars.find(c => idsEqual(c?.id, char?.id));
        const storedOutfit = scenarioChar?.outfit_id ?? scenarioChar?.outfit;
        characterOutfitId = storedOutfit != null ? String(storedOutfit) : defaultOutfitId;
      }

      const spriteUrl = await service.getScenarioSprite(
        char.id,
        scenarioId,
        '/assets/images/kiss.webp',
        characterOutfitId
      );

      const img = new Image();
      img.src = spriteUrl;

      const characterRank = await resolveCharacterRank(char.id, rankingsState, service, rankBatch);

      return toCarouselCharacter({
        id: char.id,
        name: charDetails.name || `Character ${char.id}`,
        rank: characterRank,
        modalImage: spriteUrl,
        outfitId: characterOutfitId,
        gender: normalizeGender(charDetails.gender),
        availableOutfits: scenarioOutfits
      });
    } catch (error) {
      console.error(`carousel-utils: Error fetching character ${char.id}`, error);
      const characterRank = await resolveCharacterRank(char.id, rankingsState, service, rankBatch);

      return toCarouselCharacter({
        id: char.id,
        name: `Character ${char.id}`,
        rank: characterRank,
        modalImage: '/assets/images/kiss.webp',
        outfitId: '0',
        gender: 'female',
        availableOutfits: [0]
      });
    }
  });

  return Promise.all(characterPromises);
};

export const buildConversationCarouselCharacters = async ({
  conversation,
  rankingsState,
  service
}) => {
  const { storage } = await import('../../../js/utils/storage.js');
  const lastRound = Array.isArray(conversation.lastRound) ? conversation.lastRound : [];
  const messages = conversation.messages?.length
    ? conversation.messages
    : (storage.getConversationMessages(conversation.conversationId) ?? []);

  let allCharacters = conversation.allCharacters?.length
    ? conversation.allCharacters
    : lastRound.map((entry) => ({
      id: String(entry.characterId ?? entry.character_id),
      outfitId: entry.outfitId ?? 0,
      actionId: entry.actionId ?? null
    })).filter((entry) => entry.id);

  if (!allCharacters?.length) {
    allCharacters = accumulateStateFromMessages(messages).activeCharacters;
  }

  if (!allCharacters?.length && conversation.characterId) {
    const name = rankingsState?.characters
      ? (rankingsState.characters.find(c => String(c.id) === String(conversation.characterId))?.name) || `Character ${conversation.characterId}`
      : `Character ${conversation.characterId}`;
    allCharacters = [{ id: conversation.characterId, name, outfitId: 0 }];
  }

  const { fetchCharacterSpriteUrl } = await import('../../hangout/ui/character-manager.js');
  const rankBatch = {};

  const characterPromises = allCharacters.map(async (char) => {
    try {
      let charDetails = char;
      if (rankingsState?.characters) {
        const stateCharacter = rankingsState.characters.find(c => String(c.id) === String(char.id));
        if (stateCharacter) {
          charDetails = { ...char, ...stateCharacter };
        }
      }

      let resolvedName = char.name || charDetails.name || null;
      if (isPlaceholderCharacterName(resolvedName, char.id)) {
        try {
          const fetchedDetails = await service.getCharacterDetails(char.id);
          if (fetchedDetails?.name && !isPlaceholderCharacterName(fetchedDetails.name, char.id)) {
            resolvedName = fetchedDetails.name;
            charDetails = { ...charDetails, ...fetchedDetails };
          }
        } catch (detailsError) {
          console.warn(`carousel-utils: Failed fetching character details for ${char.id}:`, detailsError);
        }
      }
      if (isPlaceholderCharacterName(resolvedName, char.id)) {
        resolvedName = `Character ${char.id}`;
      }

      const tuple = getLastRoundSpriteTuple({ ...conversation, lastRound, messages }, char.id);
      let lastAction = tuple.actionId;
      let lastOutfit = tuple.outfitId || char.outfitId || '0';

      if (lastAction === null && (conversation.lastSpriteState || conversation.lastEmotion)) {
        const parsedState = parseSpriteState(conversation.lastSpriteState || conversation.lastEmotion);
        lastAction = parsedState.action;
      }
      if (lastAction === null || lastAction === undefined || lastAction === '') {
        lastAction = getDefaultStandingActionId(charDetails.gender ?? char.gender);
      }

      const spriteUrl = await fetchCharacterSpriteUrl(char.id, lastAction, lastOutfit, { isFullSize: false });

      const img = new Image();
      img.src = spriteUrl;

      const characterRank = await resolveCharacterRank(char.id, rankingsState, service, rankBatch);

      return toCarouselCharacter({
        id: char.id,
        name: resolvedName,
        rank: characterRank,
        modalImage: spriteUrl,
        outfitId: lastOutfit,
        lastSpriteState: lastAction,
        lastAction
      });
    } catch (error) {
      console.error(`carousel-utils: Error processing conversation character ${char.id}:`, error);
      const characterRank = await resolveCharacterRank(char.id, rankingsState, service, rankBatch);

      return toCarouselCharacter({
        id: char.id,
        name: isPlaceholderCharacterName(char.name, char.id) ? `Character ${char.id}` : (char.name || `Character ${char.id}`),
        rank: characterRank,
        modalImage: '/assets/images/kiss.webp',
        outfitId: char.outfitId || 0,
        lastSpriteState: null,
        lastAction: null
      });
    }
  });

  return Promise.all(characterPromises);
};

export const MAX_PARTY_SIZE = 3;

export const normalizePartyIds = (ids, max = MAX_PARTY_SIZE, options = {}) => {
  const sortById = options.sortById !== false;
  const seen = new Set();
  const next = (ids || [])
    .filter((id) => id != null && id !== '')
    .filter((id) => {
      const key = String(id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, max);
  if (sortById && next.length >= 2) return sortIdsByNumericAsc(next);
  return next;
};

export const fillEmptyPartySlotsFromScenario = (partyIds, scenarioDetails, max = MAX_PARTY_SIZE) => {
  if (!scenarioDetails?.chars || scenarioDetails.chars.length < 2) {
    return { partyIds: normalizePartyIds(partyIds, max), changed: false };
  }

  const next = normalizePartyIds(partyIds, max);
  if (next.length >= max) return { partyIds: next, changed: false };

  const ordered = getOrderedScenarioChars(scenarioDetails);
  const keys = new Set(next.map((id) => String(id)));
  let changed = false;

  for (const scenarioChar of ordered) {
    if (next.length >= max) break;
    const id = scenarioChar?.id;
    if (id == null || keys.has(String(id))) continue;
    next.push(id);
    keys.add(String(id));
    changed = true;
  }

  return { partyIds: normalizePartyIds(next, max), changed };
};
